import React, { useState, useMemo, useEffect, useLayoutEffect, useRef, memo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  SandpackProvider,
  SandpackLayout,
  SandpackPreview
} from "@codesandbox/sandpack-react";
import { atomDark } from "@codesandbox/sandpack-themes";
import {
  Maximize2, Minimize2, Monitor, Tablet, Smartphone,
  RotateCcw, Minus, Plus, ChevronDown, Check, Sparkles,
  Tv, X, Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Slider } from '../components/ui/slider';
import Tip, { MOD } from '../components/ui/tip';
import XmlViewer from '../components/XmlViewer';
import DocToolbar, { DocFormatControl, DocActions } from './preview/DocToolbar';
import { PREVIEW_KIND, getPreviewKind, isTextDocKind } from './preview/previewKind';
import { cn } from '../lib/utils';
import { extractDependencies } from '../lib/sandboxDeps';

// How long editing pauses before the HTML preview reloads. Long enough that a
// normal typing rhythm doesn't thrash the iframe, short enough to still feel
// like a live preview.
const HTML_DEBOUNCE_MS = 400;

// Device presets for responsive testing
const DEVICE_PRESETS = [
  { id: 'responsive', label: 'Responsive', width: null, icon: Monitor },
  { id: 'desktop', label: 'Desktop (1280px)', width: 1280, icon: Monitor },
  { id: 'tablet', label: 'Tablet (768px)', width: 768, icon: Tablet },
  { id: 'mobile', label: 'Mobile (375px)', width: 375, icon: Smartphone },
];

// Custom components for Markdown to inject source line info
const createSourcePosComponent = (Tag) => ({ node, ...props }) => {
  return <Tag data-source-line={node?.position?.start?.line} {...props} />;
};

// Hoisted: a document re-renders behind every keystroke in the editor, and
// this string is the same every time.
const DOC_PROSE_CLASSES = [
  'prose prose-invert prose-sm sm:prose-base max-w-[80ch]',
  // Azeret Mono for headings, tracking-tight above 32px — design_guidelines.json
  'prose-headings:font-mono prose-headings:font-bold prose-headings:tracking-tight',
  'prose-h1:text-3xl sm:prose-h1:text-4xl',
  // Body is IBM Plex Sans at relaxed leading; this is what makes it read as a
  // document rather than a viewport.
  'prose-p:text-zinc-400 prose-p:leading-relaxed prose-li:text-zinc-400',
  'prose-strong:text-zinc-100',
  'prose-a:text-primary prose-a:no-underline hover:prose-a:underline',
  'prose-code:font-mono prose-code:text-accent prose-code:before:content-none prose-code:after:content-none',
  'prose-pre:bg-black/40 prose-pre:border prose-pre:border-white/10',
  'prose-blockquote:border-l-primary/50 prose-blockquote:text-zinc-400 prose-blockquote:not-italic',
  'prose-hr:border-white/10',
  'prose-th:border-white/10 prose-td:border-white/10 prose-th:text-zinc-100',
  'prose-img:rounded-lg prose-img:border prose-img:border-white/10',
].join(' ');

const MarkdownComponents = {
  p: createSourcePosComponent('p'),
  h1: createSourcePosComponent('h1'),
  h2: createSourcePosComponent('h2'),
  h3: createSourcePosComponent('h3'),
  h4: createSourcePosComponent('h4'),
  h5: createSourcePosComponent('h5'),
  h6: createSourcePosComponent('h6'),
  li: createSourcePosComponent('li'),
  blockquote: createSourcePosComponent('blockquote'),
  code: ({ node, inline, ...props }) => {
    // Only block code usually has meaningful line numbers for navigation
    return <code data-source-line={!inline ? node?.position?.start?.line : undefined} {...props} />;
  }
};

/**
 * The fake browser window a device-width preview sits inside.
 *
 * Extracted because the HTML branch needs the same chrome the mockup branch
 * draws, and a third hand-rolled copy of traffic lights + a localhost pill is
 * how a design system stops being one.
 */
const BrowserFrame = memo(function BrowserFrame({ style, children }) {
  return (
    <div
      className="flex flex-col overflow-hidden rounded-xl border border-white/10 bg-[#111]"
      style={style}
    >
      <div className="flex items-center gap-2 border-b border-white/5 bg-black/50 px-4 py-2">
        <div className="flex gap-1.5">
          <div className="h-3 w-3 rounded-full bg-red-500/60" />
          <div className="h-3 w-3 rounded-full bg-yellow-500/60" />
          <div className="h-3 w-3 rounded-full bg-green-500/60" />
        </div>
        <div className="mx-4 flex-1">
          <div className="rounded-md bg-white/5 px-3 py-1 text-center font-mono text-xs text-muted-foreground">
            localhost:3000
          </div>
        </div>
      </div>
      {children}
    </div>
  );
});

// Zen Mode Full-Browser Overlay component
const ZenPreviewOverlay = ({ currentDevice, devicePreset, onDeviceChange, zoom, onRefresh, onClose, contentProps }) => {
  const [showToolbar, setShowToolbar] = useState(true);
  const timerRef = useRef(null);

  const handleMouseMove = useCallback(() => {
    setShowToolbar(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setShowToolbar(false);
    }, 2500);
  }, []);

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      setShowToolbar(false);
    }, 2500);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] bg-[#09090b] w-screen h-screen overflow-hidden flex flex-col font-sans select-none"
      onMouseMove={handleMouseMove}
    >
      {/* Floating Auto-Hiding Glassmorphic Toolbar */}
      <div 
        className={cn(
          "absolute top-3 sm:top-4 left-1/2 -translate-x-1/2 z-50 transition-all duration-300 transform max-w-[calc(100vw-24px)]",
          showToolbar ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4 pointer-events-none"
        )}
      >
        <div className="flex items-center justify-between gap-2 sm:gap-3 px-3 sm:px-5 py-2 bg-black/90 backdrop-blur-2xl border border-white/15 rounded-full sm:rounded-2xl shadow-2xl w-full text-foreground">
          {/* Title Badge */}
          <div className="flex items-center gap-2 border-r border-white/10 pr-2 sm:pr-4 shrink-0">
            <div className="h-7 w-7 rounded-lg bg-violet-500/20 border border-violet-500/30 flex items-center justify-center">
              <Tv className="h-3.5 w-3.5 text-violet-400" />
            </div>
            <div className="hidden sm:flex flex-col">
              <span className="text-[11px] font-mono font-bold tracking-wider text-violet-400 uppercase leading-none">
                ZEN PREVIEW
              </span>
              <span className="text-[10px] text-muted-foreground font-mono mt-0.5">
                Full Canvas
              </span>
            </div>
          </div>

          {/* Device Presets */}
          <div className="flex items-center gap-1 sm:gap-1.5 border-r border-white/10 pr-2 sm:pr-4">
            {DEVICE_PRESETS.map((device, idx) => (
              <Tip key={device.id} label={device.label} hint={`${idx + 1}`}>
                <button
                  onClick={() => onDeviceChange(device.id)}
                  data-testid={`zen-device-${device.id}`}
                  className={cn(
                    "h-8 sm:h-7 px-2 sm:px-3 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 active:scale-95",
                    devicePreset === device.id
                      ? 'bg-violet-500/25 text-violet-300 border border-violet-500/40 shadow-sm shadow-violet-500/20'
                      : 'text-muted-foreground hover:text-foreground hover:bg-white/5 border border-transparent'
                  )}
                >
                  <device.icon className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
                  <span className="hidden md:inline-block text-xs">{device.label.split(' ')[0]}</span>
                  <span className="hidden lg:inline-block text-[10px] font-mono opacity-50">[{idx + 1}]</span>
                </button>
              </Tip>
            ))}
          </div>

          {/* Zoom & Refresh */}
          <div className="flex items-center gap-1 border-r border-white/10 pr-2 sm:pr-4 shrink-0">
            <Tip label="Refresh preview" hint={`${MOD}R`}>
              <button
                onClick={onRefresh}
                data-testid="zen-refresh"
                className="h-8 w-8 sm:h-7 sm:w-7 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/10 flex items-center justify-center transition-all active:scale-95"
              >
                <RotateCcw className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
              </button>
            </Tip>
            <span className="text-xs text-muted-foreground font-mono px-1.5 hidden sm:inline-block">
              {zoom}%
            </span>
          </div>

          {/* Exit Button & ESC Badge */}
          <div className="flex items-center gap-2 shrink-0">
            <span className="hidden md:inline-block text-[10px] text-zinc-400 font-mono px-2 py-0.5 rounded bg-white/5 border border-white/10">
              ESC
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={onClose}
              className="h-8 sm:h-7 px-2.5 sm:px-3 rounded-full sm:rounded-lg text-xs bg-red-500/15 hover:bg-red-500/25 text-red-400 border border-red-500/30 flex items-center gap-1.5 transition-all active:scale-95"
            >
              <X className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
              <span className="hidden sm:inline-block">Exit</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Main Preview Content */}
      <div className="flex-1 w-full h-full overflow-hidden">
        <PreviewContent {...contentProps} />
      </div>
    </div>,
    document.body
  );
};

// Extracted ZoomToolbar component
const ZoomToolbar = memo(({ 
  isFullscreenMode = false, 
  currentDevice, 
  devicePreset, 
  onDeviceChange, 
  zoom, 
  onZoomChange, 
  onRefresh, 
  onToggleFullscreen,
  onToggleZenMode
}) => {
  const CurrentDeviceIcon = currentDevice?.icon || Monitor;
  
  const handleZoomIn = () => onZoomChange(Math.min(zoom + 25, 200));
  const handleZoomOut = () => onZoomChange(Math.max(zoom - 25, 25));

  return (
    <div className="flex items-center justify-between px-3 py-2 bg-black/80 backdrop-blur-xl border-b border-white/10 flex-shrink-0 gap-2 overflow-x-auto no-scrollbar">
      {/* Device Dropdown - Flex-1 on mobile to fill space */}
      <div className="flex-1 md:flex-none md:hidden">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-9 w-full justify-start gap-2 px-2 text-muted-foreground hover:text-foreground border border-white/5 bg-white/5">
              <CurrentDeviceIcon className="h-4 w-4" />
              <span className="truncate">{currentDevice?.label || 'Responsive'}</span>
              <ChevronDown className="h-3 w-3 opacity-50 ml-auto" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="bg-[#111] border-white/10 w-[200px]">
            {DEVICE_PRESETS.map((device, idx) => (
              <DropdownMenuItem
                key={device.id}
                onClick={() => onDeviceChange(device.id)}
                className="gap-2 focus:bg-white/10 focus:text-white cursor-pointer"
              >
                <device.icon className="h-4 w-4" />
                <span>{device.label}</span>
                {devicePreset === device.id && <Check className="h-3 w-3 ml-auto text-primary" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Desktop: Device Icons Row */}
      <div className="hidden md:flex items-center gap-1 border-r border-white/10 pr-3 mr-1">
        {DEVICE_PRESETS.map((device, idx) => (
          // The 1–4 and ⌘R bindings only exist while expanded or in zen, so the
          // hint appears only where the key actually does something.
          <Tip key={device.id} label={device.label} hint={isFullscreenMode ? `${idx + 1}` : undefined}>
            <button
              onClick={() => onDeviceChange(device.id)}
              data-testid={`preview-device-${device.id}`}
              className={`p-1.5 rounded-lg transition-all ${devicePreset === device.id
                ? 'bg-primary/20 text-primary'
                : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                }`}
            >
              <device.icon className="h-4 w-4" />
            </button>
          </Tip>
        ))}
        {currentDevice?.width && (
          <span className="text-xs text-muted-foreground font-mono ml-1">
            {currentDevice.width}px
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        {/* Mobile: Compact Zoom Controls - Larger Touch Targets */}
        <div className="flex md:hidden items-center bg-white/5 rounded-lg p-0.5 border border-white/5">
          <Tip label="Zoom out">
            <button
              onClick={handleZoomOut}
              disabled={zoom <= 25}
              data-testid="preview-zoom-out-mobile"
              className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-white/10 disabled:opacity-30 transition-all active:bg-white/10"
            >
              <Minus className="h-4 w-4" />
            </button>
          </Tip>
          <span className="text-xs text-muted-foreground font-mono w-10 text-center select-none font-medium">
            {zoom}%
          </span>
          <Tip label="Zoom in">
            <button
              onClick={handleZoomIn}
              disabled={zoom >= 200}
              data-testid="preview-zoom-in-mobile"
              className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-white/10 disabled:opacity-30 transition-all active:bg-white/10"
            >
              <Plus className="h-4 w-4" />
            </button>
          </Tip>
        </div>

        {/* Desktop: Expanded Zoom Controls */}
        <div className="hidden md:flex items-center gap-2">
          <Tip label="Zoom out">
            <button
              onClick={handleZoomOut}
              disabled={zoom <= 25}
              data-testid="preview-zoom-out"
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              <Minus className="h-4 w-4" />
            </button>
          </Tip>

          <div className="flex items-center gap-2 min-w-[140px]">
            <Slider
              value={[zoom]}
              onValueChange={([val]) => onZoomChange(val)}
              min={25}
              max={200}
              step={5}
              className="w-20"
            />
            <span className="text-xs text-muted-foreground font-mono w-10 text-center">
              {zoom}%
            </span>
          </div>

          <Tip label="Zoom in">
            <button
              onClick={handleZoomIn}
              disabled={zoom >= 200}
              data-testid="preview-zoom-in"
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              <Plus className="h-4 w-4" />
            </button>
          </Tip>
        </div>

        {/* Desktop: Zoom Presets */}
        <div className="hidden md:flex items-center gap-1 border-l border-white/10 pl-3 ml-1">
          {[50, 100, 150].map(preset => (
            <button
              key={preset}
              onClick={() => onZoomChange(preset)}
              className={`px-2 py-1 rounded-md text-xs font-mono transition-all ${zoom === preset
                ? 'bg-primary/20 text-primary'
                : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                }`}
            >
              {preset}%
            </button>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 pl-1 md:border-l md:border-white/10 md:pl-2">
          <Tip label="Refresh preview" hint={isFullscreenMode ? `${MOD}R` : undefined}>
            <button
              onClick={onRefresh}
              data-testid="preview-refresh"
              className="p-2 md:p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-all active:bg-white/10"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          </Tip>
          {onToggleZenMode ? (
            <Tip label="Full browser window" hint={`${MOD}⇧F`}>
              <button
                onClick={onToggleZenMode}
                data-testid="preview-zen"
                className="p-2 md:p-1.5 rounded-lg text-violet-400 hover:text-violet-300 hover:bg-violet-500/20 transition-all active:bg-violet-500/30 border border-violet-500/30"
              >
                <Tv className="h-4 w-4" />
              </button>
            </Tip>
          ) : null}
          {/* Only where there's a row to fill. On mobile the preview already
              occupies the whole screen, so there'd be nothing to expand into
              and the button would be dead. */}
          {onToggleFullscreen ? (
            <Tip
              label={isFullscreenMode ? 'Collapse panel' : 'Expand to fill workspace'}
              hint={isFullscreenMode ? 'Esc' : undefined}
            >
              <button
                onClick={onToggleFullscreen}
                data-testid="preview-expand"
                className="p-2 md:p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-all active:bg-white/10"
              >
                {isFullscreenMode ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </button>
            </Tip>
          ) : null}
        </div>
      </div>
    </div>
  );
});

// Extracted Content Component to prevent re-renders and preserve scroll
const PreviewContent = memo(({
  file,
  kind,
  zoom,
  devicePreset,
  currentDevice,
  refreshKey,
  dependencies,
  onSyncEditor,
  // Only reach the XML branch, which hands them to XmlViewer's own toolbar.
  // Flat rather than a `docControls` object so memo can still compare them.
  readOnly,
  onChangeFormat,
  isExpanded,
  onToggleExpand
}) => {
  const [tooltipPos, setTooltipPos] = useState(null);
  const [selectedLine, setSelectedLine] = useState(null);
  const containerRef = useRef(null);

  // Scroll preservation
  const scrollRef = useRef(0);

  const handleScroll = useCallback((e) => {
    scrollRef.current = e.target.scrollTop;
  }, []);

  // ── HTML settling ────────────────────────────────────────────────────────
  // `file.content` changes on every keystroke and re-assigning an iframe's
  // srcDoc reloads the document — flicker, scripts re-run, scroll lost. So the
  // HTML branch renders a settled copy rather than the live one. Every other
  // kind ignores this entirely.
  const isHtml = kind === PREVIEW_KIND.HTML;
  const settledNameRef = useRef(null);
  const [settledHtml, setSettledHtml] = useState('');

  useEffect(() => {
    if (!isHtml) return undefined;

    // Opening a *different* file must swap at once — waiting 400ms there would
    // just show the previous page. Only edits to the same file are debounced.
    if (settledNameRef.current !== file.name) {
      settledNameRef.current = file.name;
      setSettledHtml(file.content || '');
      return undefined;
    }

    const timer = setTimeout(() => setSettledHtml(file.content || ''), HTML_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [isHtml, file.name, file.content]);

  useLayoutEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = scrollRef.current;
    }
  }, [file.content, file.name]); // Restore on content change

  // Calculate frame dimensions
  const getFrameStyle = () => {
    if (currentDevice?.width) {
      return {
        width: `${currentDevice.width}px`,
        maxWidth: '100%',
        margin: '0 auto',
      };
    }
    return {
      width: '100%',
      height: '100%',
    };
  };

  const zoomStyle = {
    zoom: zoom / 100,
    MozTransform: `scale(${zoom / 100})`,
    MozTransformOrigin: '0 0',
  };

  // Handle text selection for AI tooltip
  const handleMouseUp = useCallback(() => {
    if (!onSyncEditor) return;

    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) {
      setTooltipPos(null);
      setSelectedLine(null);
      return;
    }

    // Try to find source line
    let node = selection.anchorNode;
    let foundLine = null;
    
    // Traverse up to find element with data-source-line
    while (node && node !== document.body) {
      if (node.nodeType === 1 && node.hasAttribute('data-source-line')) {
        foundLine = parseInt(node.getAttribute('data-source-line'), 10);
        break;
      }
      node = node.parentNode;
    }

    if (foundLine) {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      const containerRect = containerRef.current?.getBoundingClientRect();
      
      if (containerRect) {
        setTooltipPos({
          top: rect.top - containerRect.top - 40, // Position above selection
          left: rect.left - containerRect.left
        });
        setSelectedLine(foundLine);
      }
    } else {
      setTooltipPos(null);
      setSelectedLine(null);
    }
  }, [onSyncEditor]);

  // Handle AI action
  const handleAIAction = () => {
    if (selectedLine && onSyncEditor) {
      onSyncEditor(selectedLine);
      setTooltipPos(null);
      
      // Clear selection
      const selection = window.getSelection();
      if (selection) selection.removeAllRanges();
    }
  };

  // ── Text documents ───────────────────────────────────────────────────────
  // No frame, no zoom, no device width. A document reflows, so wrapping it in
  // a simulated viewport was only ever adding borders. `zoomStyle` and
  // `getFrameStyle()` below belong to the mockup and asset branches only.

  if (kind === PREVIEW_KIND.XML) {
    // XmlViewer already owns a toolbar with search, tree/raw and copy, so the
    // panel's own controls are injected into it rather than stacked above it.
    return (
      <div className="h-full w-full overflow-hidden bg-[#0d0d0d]">
        <XmlViewer
          content={file.content || ''}
          fileName={file.name}
          toolbarLeading={
            <DocFormatControl file={file} readOnly={readOnly} onChangeFormat={onChangeFormat} />
          }
          toolbarTrailing={
            <DocActions
              file={file}
              isExpanded={isExpanded}
              onToggleExpand={onToggleExpand}
              showCopy={false}
            />
          }
        />
      </div>
    );
  }

  if (kind === PREVIEW_KIND.MARKDOWN) {
    return (
      // The outer element does not scroll: it exists so the "Edit in Source"
      // tooltip can be positioned against the viewport-fixed box. tooltipPos is
      // computed as a viewport delta, so anchoring it inside the scrolled box
      // made it drift by scrollTop.
      <div className="relative h-full w-full overflow-hidden bg-[#0d0d0d]">
        <div
          ref={containerRef}
          onScroll={handleScroll}
          className="h-full w-full overflow-auto"
        >
          {/* Padding scales with the panel so a 380px column isn't cramped and
              an expanded one isn't gutterless. */}
          <div onMouseUp={handleMouseUp} className="px-6 py-8 sm:px-8 sm:py-10 lg:px-12">
            <article className={DOC_PROSE_CLASSES}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={MarkdownComponents}
              >
                {file.content}
              </ReactMarkdown>
            </article>
          </div>
        </div>

        {/* AI Tooltip */}
        {tooltipPos ? (
          <div
            className="absolute z-50 animate-in fade-in zoom-in-95 duration-200"
            style={{ top: Math.max(0, tooltipPos.top), left: Math.max(0, tooltipPos.left) }}
          >
            <Button
              size="sm"
              onClick={handleAIAction}
              className="h-8 shadow-xl bg-violet-600 hover:bg-violet-700 text-white border-violet-500 gap-2"
            >
              <Sparkles className="h-3 w-3" />
              Edit in Source
            </Button>
          </div>
        ) : null}
      </div>
    );
  }

  if (kind === PREVIEW_KIND.MOCKUP) {
    if (!currentDevice?.width) {
      return (
        <div className="h-full w-full bg-[#0d0d0d] flex flex-col" style={zoomStyle}>
          <SandpackProvider
            key={refreshKey}
            template="react"
            theme={atomDark}
            files={{ "/App.js": file.content }}
            options={{ externalResources: ["https://cdn.tailwindcss.com"] }}
            customSetup={{ dependencies: dependencies }}
            style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
          >
            <SandpackLayout style={{
              border: 'none',
              borderRadius: 0,
              backgroundColor: 'transparent',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
            }}>
              <SandpackPreview
                style={{ flex: 1 }}
                showOpenInCodeSandbox={false}
                showRefreshButton={false}
              />
            </SandpackLayout>
          </SandpackProvider>
        </div>
      );
    }

    return (
      <div className="h-full w-full bg-[#0d0d0d] overflow-auto flex justify-center p-4">
        <BrowserFrame style={{ ...getFrameStyle(), height: 'fit-content', minHeight: '500px' }}>
          <div className="flex-1 overflow-auto origin-top-left" style={zoomStyle}>
            <SandpackProvider
              key={refreshKey}
              template="react"
              theme={atomDark}
              files={{ "/App.js": file.content }}
              options={{ externalResources: ["https://cdn.tailwindcss.com"] }}
              customSetup={{ dependencies: dependencies }}
            >
              <SandpackLayout style={{
                border: 'none',
                borderRadius: 0,
                backgroundColor: 'transparent',
                minHeight: '400px',
              }}>
                <SandpackPreview
                  style={{ minHeight: '400px' }}
                  showOpenInCodeSandbox={false}
                  showRefreshButton={false}
                />
              </SandpackLayout>
            </SandpackProvider>
          </div>
        </BrowserFrame>
      </div>
    );
  }

  // ── HTML ─────────────────────────────────────────────────────────────────
  // A web page is a viewport, not a document, so it keeps the device presets,
  // zoom, reload and zen that markdown gave up — same toolbar as a mockup.
  if (kind === PREVIEW_KIND.HTML) {
    const frame = (
      <iframe
        // Reload remounts the element, which is the only way to re-run scripts
        // and reset the document without touching the source.
        key={refreshKey}
        srcDoc={settledHtml}
        title={file.name}
        // `allow-scripts` WITHOUT `allow-same-origin` puts the page on a unique
        // opaque origin: its scripts run, but it cannot read the app's cookies,
        // localStorage or DOM. Granting both together would let it remove its
        // own sandbox, which is the one combination to avoid.
        sandbox="allow-scripts"
        data-testid="html-preview-frame"
        // White, not #0d0d0d: an unstyled page on a dark surface reads as
        // broken. The preview has to be faithful, so nothing is injected.
        className="h-full w-full border-0 bg-white"
      />
    );

    if (!currentDevice?.width) {
      return (
        <div className="h-full w-full bg-[#0d0d0d]" style={zoomStyle}>
          {frame}
        </div>
      );
    }

    return (
      <div className="h-full w-full bg-[#0d0d0d] overflow-auto flex justify-center p-4">
        <BrowserFrame style={{ ...getFrameStyle(), height: 'fit-content', minHeight: '500px' }}>
          <div className="flex-1 origin-top-left" style={{ ...zoomStyle, minHeight: '400px' }}>
            {frame}
          </div>
        </BrowserFrame>
      </div>
    );
  }

  if (kind === PREVIEW_KIND.ASSET) {
    return (
      <div className="h-full w-full flex items-center justify-center p-8 bg-[#0d0d0d] overflow-auto">
        <div style={zoomStyle} className="origin-center">
          {file.content.startsWith('data:') || file.content.startsWith('http') ? (
            <img src={file.content} alt={file.name} className="max-w-full rounded-md shadow-2xl border border-white/10" />
          ) : (
            <div className="text-center text-muted-foreground">
              <p>Asset preview not available for raw content.</p>
            </div>
          )}
        </div>
      </div>
    )
  }

  return <div className="p-8 text-muted-foreground bg-[#0d0d0d] h-full flex items-center justify-center">No preview available.</div>;
});

/**
 * @param {boolean}  isExpanded     Whether the preview surface currently fills
 *                                  the whole workspace row. Owned by Workspace,
 *                                  because expanding means hiding a *sibling*
 *                                  surface — which this component can't see.
 * @param {Function} onToggleExpand Flip that state.
 * @param {boolean}  readOnly       Suppresses the format switcher on shared
 *                                  projects.
 * @param {Function} onChangeFormat (ext) => void. Renames the document, which
 *                                  is what "switching format" means here —
 *                                  content is never rewritten. Owned by
 *                                  Workspace because it's a file mutation.
 */
export default function Preview({
  file,
  projectId,
  onSyncEditor,
  isExpanded = false,
  onToggleExpand,
  readOnly = false,
  onChangeFormat,
  hideDocToolbar = false,
}) {
  const ZOOM_KEY = `forge-ai-preview-zoom-${projectId}`;
  const DEVICE_KEY = `forge-ai-preview-device-${projectId}`;

  const [isZenMode, setIsZenMode] = useState(false);
  const [zoom, setZoom] = useState(() => {
    const saved = localStorage.getItem(ZOOM_KEY);
    return saved ? parseInt(saved, 10) : 100;
  });
  const [devicePreset, setDevicePreset] = useState(() => {
    return localStorage.getItem(DEVICE_KEY) || 'responsive';
  });
  const [refreshKey, setRefreshKey] = useState(0);

  const [isTransitioning, setIsTransitioning] = useState(false);
  const prevFileNameRef = useRef(file?.name);

  useEffect(() => {
    if (file?.name && prevFileNameRef.current !== file.name) {
      setIsTransitioning(true);
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 50);
      prevFileNameRef.current = file.name;
      return () => clearTimeout(timer);
    }
  }, [file?.name]);

  const handleZoomChange = useCallback((newZoom) => {
    setZoom(newZoom);
    localStorage.setItem(ZOOM_KEY, String(newZoom));
  }, [ZOOM_KEY]);

  const handleDeviceChange = useCallback((newDevice) => {
    setDevicePreset(newDevice);
    localStorage.setItem(DEVICE_KEY, newDevice);
  }, [DEVICE_KEY]);

  const dependencies = useMemo(() => extractDependencies(file?.content), [file?.content]);
  const currentDevice = DEVICE_PRESETS.find(d => d.id === devicePreset);

  // Keyed on primitives, not on `file` — that object is replaced on every
  // keystroke, and the kind can only change when the name or type does.
  const kind = useMemo(() => getPreviewKind(file), [file?.name, file?.type]);
  const isTextDoc = isTextDocKind(kind);

  const handleRefresh = useCallback(() => setRefreshKey(prev => prev + 1), []);
  const handleToggleZenMode = useCallback(() => setIsZenMode(prev => !prev), []);

  // Global Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      const activeEl = document.activeElement;
      const isInput = activeEl && (
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(activeEl.tagName) || 
        activeEl.isContentEditable ||
        activeEl.classList?.contains('monaco-editor')
      );

      // Cmd+Shift+F or Ctrl+Shift+F -> Toggle Zen mode.
      // Zen is a full-browser canvas for a rendered mockup; a document has no
      // canvas to give it, so the shortcut stays unbound there.
      if (!isTextDoc && (e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        setIsZenMode(prev => !prev);
        return;
      }

      // Esc -> leave Zen, else collapse the expanded surface
      if (e.key === 'Escape') {
        if (isZenMode) {
          setIsZenMode(false);
          e.preventDefault();
        } else if (isExpanded) {
          onToggleExpand?.();
          e.preventDefault();
        }
        return;
      }

      // Intercept 1-4 / Cmd+R only when preview active or inside Zen/expanded.
      // Never over a document: device presets and the Sandpack remount do
      // nothing there, so all this did was swallow keystrokes.
      if (!isInput && !isTextDoc && (isZenMode || isExpanded)) {
        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'r') {
          e.preventDefault();
          handleRefresh();
        } else if (e.key === '1') {
          handleDeviceChange('responsive');
        } else if (e.key === '2') {
          handleDeviceChange('desktop');
        } else if (e.key === '3') {
          handleDeviceChange('tablet');
        } else if (e.key === '4') {
          handleDeviceChange('mobile');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isZenMode, isExpanded, isTextDoc, onToggleExpand, handleRefresh, handleDeviceChange]);

  if (!file) return null;

  const contentProps = {
    file,
    kind,
    zoom,
    devicePreset,
    currentDevice,
    refreshKey,
    dependencies,
    onSyncEditor,
    readOnly,
    onChangeFormat,
    isExpanded,
    onToggleExpand
  };

  return (
    <>
      {/* One tree, expanded or not. This used to be duplicated into a Dialog
          for fullscreen, which meant two Sandpack instances of the same file
          and two copies of the toolbar to keep in sync. Expansion is now a
          layout concern owned by Workspace, so this renders once. */}
      <div className="flex h-full w-full min-h-0 flex-col">
        {/* A text document and a mockup have almost nothing in common to
            control, so they get different instrument strips rather than one
            strip with most of its controls greyed out. XML gets no strip here
            at all — it composes its controls into XmlViewer's own bar, so the
            panel never shows two stacked toolbars. */}
        {kind === PREVIEW_KIND.MARKDOWN ? (
          hideDocToolbar ? null : (
            <DocToolbar
              file={file}
              readOnly={readOnly}
              onChangeFormat={onChangeFormat}
              isExpanded={isExpanded}
              onToggleExpand={onToggleExpand}
            />
          )
        ) : isTextDoc ? null : (
          <ZoomToolbar
            isFullscreenMode={isExpanded}
            currentDevice={currentDevice}
            devicePreset={devicePreset}
            onDeviceChange={handleDeviceChange}
            zoom={zoom}
            onZoomChange={handleZoomChange}
            onRefresh={handleRefresh}
            onToggleFullscreen={onToggleExpand}
            onToggleZenMode={handleToggleZenMode}
          />
        )}
        {/* The dark canvas lives here rather than on the root, so the toolbar
            above stays translucent against the surface material. */}
        <div className="min-h-0 flex-1 overflow-hidden bg-[#0a0a0a]">
          <div
            className="h-full w-full transition-all duration-200 ease-out"
            style={{
              opacity: isTransitioning ? 0 : 1,
              transform: isTransitioning ? 'scale(0.98)' : 'scale(1)',
            }}
          >
            <PreviewContent {...contentProps} />
          </div>
        </div>
      </div>

      {/* Zen Mode Pure Full-Browser Canvas — mockups and assets only */}
      {isZenMode && !isTextDoc && (
        <ZenPreviewOverlay
          currentDevice={currentDevice}
          devicePreset={devicePreset}
          onDeviceChange={handleDeviceChange}
          zoom={zoom}
          onRefresh={handleRefresh}
          onClose={() => setIsZenMode(false)}
          contentProps={contentProps}
        />
      )}
    </>
  );
}
